import { Component } from "@theme/component";
import { SlideshowSelectEvent } from "@theme/events";
import { Scroller, scrollIntoView } from "@theme/scrolling";
import {
  center,
  closest,
  clamp,
  mediaQueryLarge,
  prefersReducedMotion,
  preventDefault,
  viewTransition,
  scheduler,
} from "@theme/utilities";

// The threshold for determining visibility of slides.
const SLIDE_VISIBLITY_THRESHOLD = 0.7;

const ATTR_INITIAL_SLIDE = "initial-slide";
const ATTR_SLIDE_ID = "slide-id";
const ATTR_ARIA_HIDDEN = "aria-hidden";
const ATTR_INERT = "inert";
const ATTR_ARIA_LABEL = "aria-label";
const ATTR_AUTO_HIDE_CONTROLS = "auto-hide-controls";
const ATTR_SLIDESHOW_COMPONENT = "slideshow-component";
const ATTR_REVEAL = "reveal";
const ATTR_IN_VIEWPORT = "in-viewport";

/**
 * Shared viewport observer manager for lazy scroll enablement.
 *
 * Limit the number of compositor layers created by slideshows by only enabling scrolling when the slideshow is in the viewport.
 * Resolves known issues with iOS Safari where too many composition layers will crash the page.
 * When a slideshow is NOT in the viewport, it has overflow: hidden (no compositor layer).
 * When a slideshow enters the viewport, the [in-viewport] attribute is added, enabling scrolling.
 */
class SlideshowViewportObserver {
  /** @type {SlideshowViewportObserver | null} */
  static #instance = null;

  /** @type {IntersectionObserver | null} */
  #observer = null;

  /**
   * Gets the singleton instance
   * @returns {SlideshowViewportObserver}
   */
  static getInstance() {
    if (!this.#instance) {
      this.#instance = new SlideshowViewportObserver();
    }
    return this.#instance;
  }

  /**
   * Registers a slideshow to be observed for viewport visibility
   * @param {Slideshow} slideshow - The slideshow to observe
   */
  observe(slideshow) {
    if (!this.#observer) {
      this.#observer = new IntersectionObserver(
        entries => {
          for (const entry of entries) {
            const slideshowElement = /** @type {Slideshow} */ (entry.target);
            if (entry.isIntersecting) {
              slideshowElement.setAttribute(ATTR_IN_VIEWPORT, "");
            } else {
              slideshowElement.removeAttribute(ATTR_IN_VIEWPORT);
            }
          }
        },
        {
          rootMargin: "100px",
        },
      );
    }

    this.#observer.observe(slideshow);
  }

  /**
   * Unregisters a slideshow from viewport observation
   * @param {Slideshow} slideshow - The slideshow to unobserve
   */
  unobserve(slideshow) {
    this.#observer?.unobserve(slideshow);
    slideshow.removeAttribute(ATTR_IN_VIEWPORT);
  }
}

/**
 * Slideshow custom element that allows sliding between content.
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} scroller
 * @property {HTMLElement} slideshowContainer
 * @property {HTMLElement[]} [slides]
 * @property {HTMLElement} [current]
 * @property {HTMLElement[]} [thumbnails]
 * @property {HTMLElement[]} [dots]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
export class Slideshow extends Component {
  static get observedAttributes() {
    return [ATTR_INITIAL_SLIDE];
  }

  requiredRefs = ["scroller"];
  /**
   * The current slide index.
   * @type {number}
   */
  #current = 0;
  /**
   * Indicates whether the slideshow is temporarily disabled (e.g., during infinite loop transition).
   * @type {boolean}
   */
  #disabled = false;
  /**
   * The interval ID for automatic playback.
   * @type {number|undefined}
   */
  #interval = undefined;
  /**
   * The Scroller instance that manages scrolling.
   * @type {Scroller | undefined}
   */
  #scroll;
  /**
   * The ResizeObserver instance for monitoring scroller size changes
   * @type {ResizeObserver | undefined}
   */
  #resizeObserver;
  /**
   * IntersectionObserver for efficient visibility tracking of slides
   * @type {IntersectionObserver | null}
   */
  #intersectionObserver = null;
  /**
   * Cached visible slides result from IntersectionObserver
   * @type {HTMLElement[]}
   */
  #visibleSlides = [];
  /**
   * Whether the aria-label currently on the scroller was applied by this component (vs. an
   * author-provided one). Gates the cleanup in #updateScrollerFocusability so we only ever
   * remove a label we own.
   * @type {boolean}
   */
  #scrollerLabelApplied = false;
  /**
   * Callback invoked on user initiated scroll to sync the current slide index
   * and emit a slide change event if the index has changed.
   */
  #handleScroll = () => {
    const previousIndex = this.#current;
    const index = this.#sync();

    if (index === previousIndex) {
      return;
    }

    const slide = this.slides?.[index];
    if (!slide) {
      return;
    }

    this.dispatchEvent(
      new SlideshowSelectEvent({
        index,
        previousIndex,
        slide,
        userInitiated: true,
        trigger: "scroll",
        id: slide.getAttribute(ATTR_SLIDE_ID),
      }),
    );
  };

  #onTransitionInit = () => {
    this.setAttribute("transitioning", "");
  };

  #onTransitionEnd = () => {
    this.#updateVisibleSlides();
    this.removeAttribute("transitioning");
  };

  /**
   * Synchronizes the scroll position and updates the current slide index.
   * @returns {number} The index of the current slide.
   */
  #sync = () => {
    const { slides } = this;
    if (!slides) {
      this.current = 0;
      return this.current;
    }

    if (!this.#scroll) {
      this.current = 0;
      return this.current;
    }

    const visibleSlides = this.visibleSlides;

    if (visibleSlides.length === 0) {
      return this.current;
    }

    const { axis } = this.#scroll;
    const { scroller } = this.refs;
    const centers = visibleSlides.map(slide => center(slide, axis));
    const referencePoint = visibleSlides.length > 1 ? scroller.getBoundingClientRect()[axis] : center(scroller, axis);
    const closestCenter = closest(centers, referencePoint);
    const closestVisibleSlide = visibleSlides[centers.indexOf(/** @type {number} */ (closestCenter))];

    if (!closestVisibleSlide) {
      this.current = 0;
      return this.current;
    }

    const index = slides.indexOf(closestVisibleSlide);
    this.current = index;
    return this.current;
  };

  #dragging = false;
  /**
   * Handles the 'mousedown' event to start dragging slides.
   * @param {MouseEvent} event - The mousedown event.
   */
  #handleMouseDown = event => {
    const { slides } = this;

    if (!slides || slides.length <= 1) {
      return;
    }
    if (!(event.target instanceof Element)) {
      return;
    }
    if (this.disabled || this.#dragging) {
      return;
    }

    // Check if the event target is within a 3D model interactive element
    // This prevents the slideshow from capturing drag events when interacting with 3D models
    if (event.target.closest("model-viewer")) {
      return;
    }

    event.preventDefault();
    // Store initial position but don't start handling yet
    if (!this.#scroll) {
      return;
    }
    const { axis } = this.#scroll;
    const startPosition = /** @type {any} */ (event)[axis];

    const controller = new AbortController();
    const { signal } = controller;
    const startTime = performance.now();
    let previous = startPosition;
    let velocity = 0;
    let moved = false;
    let distanceTravelled = 0;

    this.#dragging = true;

    /**
     * Handles the 'pointermove' event to update the scroll position.
     * @param {PointerEvent} pointerEvent - The pointermove event.
     */
    const onPointerMove = pointerEvent => {
      const current = /** @type {any} */ (pointerEvent)[axis];
      const initialDelta = startPosition - current;

      if (!initialDelta) {
        return;
      }

      if (!moved) {
        moved = true;
        this.setPointerCapture(pointerEvent.pointerId);

        // Prevent clicks once the user starts dragging
        document.addEventListener("click", preventDefault, { once: true, signal });

        const movingRight = initialDelta < 0;
        const movingLeft = initialDelta > 0;

        // Check if the current slideshow should handle this drag
        const closestSlideshow = this.parentElement?.closest(ATTR_SLIDESHOW_COMPONENT);
        const isNested = closestSlideshow instanceof Slideshow && closestSlideshow !== this;
        const cannotMoveInDirection = (movingRight && this.atStart) || (movingLeft && this.atEnd);

        // Abort and let the parent slideshow handle the drag if we're moving in a direction where nested slideshow can't move
        if (isNested && cannotMoveInDirection) {
          controller.abort();
          return;
        }

        this.pause();
        this.setAttribute("dragging", "");
      }

      // Stop the event from bubbling up to parent slideshow components
      pointerEvent.stopImmediatePropagation();

      const delta = previous - current;
      const timeDelta = performance.now() - startTime;
      velocity = Math.round((delta / timeDelta) * 1000);
      previous = current;
      distanceTravelled += Math.abs(delta);

      this.#scroll?.by(delta, { instant: true });
    };

    /**
     * Handles the 'pointerup' event to stop dragging slides.
     * @param {PointerEvent} pointerEvent - The pointerup event.
     */
    const onPointerUp = async pointerEvent => {
      controller.abort();
      const { current } = this;
      const { scroller } = this.refs;

      this.#dragging = false;

      if (!slides?.length || !scroller) {
        return;
      }

      const direction = Math.sign(velocity);
      const next = this.#sync();

      const modifier = current !== next || Math.abs(velocity) < 10 || distanceTravelled < 10 ? 0 : direction;
      const newIndex = clamp(next + modifier, 0, slides.length - 1);

      const newSlide = slides[newIndex];
      const currentIndex = this.current;

      if (!newSlide) {
        throw new Error(`Slide not found at index ${newIndex}`);
      }

      this.#scroll?.to(newSlide);

      this.removeAttribute("dragging");
      this.releasePointerCapture(pointerEvent.pointerId);

      this.#centerSelectedThumbnail(newIndex);

      this.dispatchEvent(
        new SlideshowSelectEvent({
          index: newIndex,
          previousIndex: currentIndex,
          userInitiated: true,
          trigger: "drag",
          slide: newSlide,
          id: newSlide.getAttribute(ATTR_SLIDE_ID),
        }),
      );

      this.current = newIndex;

      await this.#scroll?.finished;

      // It's possible that the user started dragging again before the scroll finished
      if (this.#dragging) {
        return;
      }

      // Re-enable scroll-snap that was disabled at drag start so native swiping snaps
      // to slide boundaries again.
      if (this.#scroll) {
        this.#scroll.snap = true;
      }
      this.resume();
    };

    this.#scroll.snap = false;

    document.addEventListener("pointermove", onPointerMove, { signal });
    document.addEventListener("pointerup", onPointerUp, { signal });
    /** End the gesture when a zoom-dialog tap-and-drag is captured by these listeners. */
    document.addEventListener("pointerdown", onPointerUp, { signal });
    document.addEventListener("pointercancel", onPointerUp, { signal });
    // The Pointer Events event is named "lostpointercapture". It dispatches at the Document when
    // the captured node is removed mid-drag, making this the cleanup path for that case.
    document.addEventListener("lostpointercapture", onPointerUp, { signal });
  };

  #handlePointerEnter = () => {
    this.setAttribute("actioned", "");
  };

  /**
   * Pause the slideshow when the page is hidden.
   */
  #handleVisibilityChange = () => (document.hidden ? this.suspend() : this.resume());

  /**
   * Arrow-key navigation for keyboard focus anywhere inside this slideshow (a focusable
   * slide, a thumbnail, or an arrow button): Left/Right mirror the previous/next controls
   * and Home/End jump to the ends. Typing surfaces, modifier combinations, and nested
   * slideshows are left alone. When the origin was a focusable slide, focus follows the
   * selection so the next key press still reaches the slideshow after the previous slide
   * turns inert off-screen.
   * @param {KeyboardEvent} event
   */
  #handleKeyDown = async event => {
    if (event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) {
      return;
    }
    const target = event.target;
    if (!(target instanceof Element) || target.closest("slideshow-component") !== this) {
      return;
    }
    if (target.closest("input, textarea, select, [contenteditable]:not([contenteditable='false'])")) {
      return;
    }
    const slides = this.refs.slides;
    if (this.disabled || !slides || slides.length <= 1) {
      return;
    }

    const rtl = getComputedStyle(this).direction === "rtl";
    /** @type {number | null} */
    let index = null;
    switch (event.key) {
      case "ArrowRight": {
        index = rtl ? this.previousIndex : this.nextIndex;
        break;
      }
      case "ArrowLeft": {
        index = rtl ? this.nextIndex : this.previousIndex;
        break;
      }
      case "Home": {
        index = 0;
        break;
      }
      case "End": {
        index = slides.length - 1;
        break;
      }
      default: {
        return;
      }
    }

    event.preventDefault();
    const originSlide = target.closest("slideshow-slide");
    await this.select(index, event);

    const selectedSlide = this.refs.slides?.[this.current];
    if (originSlide && selectedSlide instanceof HTMLElement && selectedSlide.tabIndex >= 0) {
      selectedSlide.focus({ preventScroll: true });
    }
  };

  /**
   * @param {string} name
   * @param {string} oldValue
   * @param {string} newValue
   */
  attributeChangedCallback(name, oldValue, newValue) {
    // Collection page filtering will Morph slideshow galleries in place, updating
    // the slideshow[initial-slide] and slideshow-slide[hidden] attributes.
    // We need to re-select() the slide after the morph is complete, but not before
    // slideshow-slide elements have their [hidden] attribute updated.
    if (name === ATTR_INITIAL_SLIDE && oldValue !== newValue) {
      queueMicrotask(() => {
        // Only select if the component is connected and initialized
        if (!this.isConnected || !this.#scroll || !this.refs.slides) {
          return;
        }
        const index = Number.parseInt(newValue, 10) || 0;
        const slideId = this.refs.slides[index]?.getAttribute(ATTR_SLIDE_ID);
        if (slideId) {
          this.select({ id: slideId }, undefined, { animate: false });
        }
      });
    }
  }

  /**
   * Section Rendering morphs sync attributes back to the server markup (scroller tabindex="-1",
   * no aria-label) without reconnecting the component or resizing its container, which would
   * silently undo the focusability state. Re-derive it after every re-render.
   */
  updatedCallback() {
    super.updatedCallback();
    this.#updateScrollerFocusability();
  }

  async connectedCallback() {
    super.connectedCallback();

    // Register with shared viewport observer for lazy scroll enablement.
    // This prevents iOS Safari crashes caused by too many compositor layers.
    SlideshowViewportObserver.getInstance().observe(this);

    // Wait for any in-progress view transitions to finish
    if (viewTransition.current) {
      await viewTransition.current;
      // It's possible that the slideshow was disconnected before the view transition finished
      if (!this.isConnected) {
        return;
      }
    }

    const slideCount = this.slides?.length || 0;
    if (slideCount <= 1) {
      this.#setupSlideshowWithoutControls();
    } else {
      this.#setupSlideshow();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    // Unregister from shared viewport observer
    SlideshowViewportObserver.getInstance().unobserve(this);

    // Clear any active autoplay interval
    this.suspend();

    if (this.#scroll) {
      const { scroller } = this.refs;
      scroller.removeEventListener("mousedown", this.#handleMouseDown);
      this.#scroll.destroy();
    }

    const slideCount = this.slides?.length || 0;
    if (slideCount > 1) {
      this.removeEventListener("mouseenter", this.suspend);
      this.removeEventListener("mouseleave", this.resume);
      this.removeEventListener("pointerenter", this.#handlePointerEnter);
      this.removeEventListener("keydown", this.#handleKeyDown);
      document.removeEventListener("visibilitychange", this.#handleVisibilityChange);
    }

    if (this.#resizeObserver) {
      this.#resizeObserver.disconnect();
    }

    if (this.#intersectionObserver) {
      this.#intersectionObserver.disconnect();
      this.#intersectionObserver = null;
    }
  }

  /**
   * @param {Element} targetSlide
   * @param {Element} currentSlide
   * @param {number} requestedIndex
   * @param {number} current
   */
  async #handleNonAdjacentSlideTransition(targetSlide, currentSlide, requestedIndex, current) {
    this.#disabled = true;
    // ensure we're not mid-scroll
    await this.#scroll?.finished;

    // Create a placeholder in the original DOM position of targetSlide
    const placeholder = document.createElement("slideshow-slide");
    targetSlide.before(placeholder);

    // Decide whether targetSlide goes before or after currentSlide
    // so that we scroll a short distance in the correct direction
    if (requestedIndex < current) {
      currentSlide.before(targetSlide);
    } else {
      currentSlide.after(targetSlide);
    }

    if (current === 0) {
      this.#scroll?.to(/** @type {HTMLElement} */ (currentSlide), { instant: true });
    }

    // Once that scroll finishes, restore the DOM
    queueMicrotask(async () => {
      await this.#scroll?.finished;
      this.#disabled = false;

      // Restore the slide back to its original position. This triggers a scroll event.
      placeholder.replaceWith(targetSlide);

      // Instantly scroll to the target slide as its position will have changed
      this.#scroll?.to(/** @type {HTMLElement} */ (targetSlide), { instant: true });

      // Force Safari to recalculate the timeline state on timeline refresh (after loop)
      requestAnimationFrame(() => {
        this.setAttribute("refreshing-timeline", "");
        requestAnimationFrame(() => {
          this.removeAttribute("refreshing-timeline");
        });
      });
    });
  }

  /** Indicates whether the slideshow is nested inside another slideshow. */
  get isNested() {
    return this.parentElement?.closest(ATTR_SLIDESHOW_COMPONENT) !== null;
  }

  get initialSlide() {
    return this.refs.slides?.[this.initialSlideIndex];
  }

  /**
   * Selects a slide based on the input index.
   * @param {number|string|{id: string}} input - The index or id of the slide to select.
   * @param {Event} [event] - The event that triggered the selection.
   * @param {Object} [options] - The options for the selection.
   * @param {boolean} [options.animate=true] - Whether to animate the selection.
   */
  async select(input, event, options = {}) {
    if (this.#disabled || !this.refs.slides?.length) {
      return;
    }
    if (!this.#scroll) {
      return;
    }

    // Store the actual current slide before any mutations
    const currentSlide = this.slides?.[this.current];

    for (const revealSlide of this.refs.slides) {
      if (revealSlide.hasAttribute(ATTR_REVEAL)) {
        revealSlide.removeAttribute(ATTR_REVEAL);
        revealSlide.setAttribute(ATTR_ARIA_HIDDEN, "true");
        revealSlide.toggleAttribute(ATTR_INERT, true);
      }
    }

    // Figure out the raw desired index (could be -1 if user is on first slide and clicks prev)
    let requestedIndex = (() => {
      if (typeof input === "number") {
        return input;
      }
      if (typeof input === "string") {
        return Number.parseInt(input, 10);
      }
      if (input !== null && typeof input === "object" && "id" in input) {
        const requestedSlide = this.refs.slides.find(
          candidateSlide => candidateSlide.getAttribute(ATTR_SLIDE_ID) === input.id,
        );

        if (!requestedSlide || !this.slides) {
          return null;
        }

        // Force the slide to be revealed if it is hidden
        if (requestedSlide.hasAttribute("hidden")) {
          requestedSlide.setAttribute(ATTR_REVEAL, "");
          requestedSlide.setAttribute(ATTR_ARIA_HIDDEN, "false");
          requestedSlide.toggleAttribute(ATTR_INERT, false);
        }

        return this.slides.indexOf(requestedSlide);
      }
      return null;
    })();

    const { current } = this;
    const { slides } = this;

    // Guard checks: no slides, invalid index, or selecting the same slide
    if (!slides?.length || requestedIndex === null || Number.isNaN(requestedIndex)) {
      return;
    }

    const requestedSlideElement = slides?.[requestedIndex];
    if (currentSlide === requestedSlideElement) {
      return;
    }

    if (!this.infinite) {
      requestedIndex = clamp(requestedIndex, 0, slides.length - 1);
    }

    event?.preventDefault();

    const { animate = true } = options;
    const lastIndex = slides.length - 1;

    // Decide the actual target index (wrap for infinite loop)
    const index = (() => {
      if (requestedIndex < 0) {
        return lastIndex;
      }
      if (requestedIndex > lastIndex) {
        return 0;
      }
      return requestedIndex;
    })();

    const isAdjacentSlide = Math.abs(index - current) <= 1 && requestedIndex >= 0 && requestedIndex <= lastIndex;
    const { visibleSlides } = this;
    const instant = prefersReducedMotion() || !animate;

    const targetSlide = slides[index];
    if (!targetSlide) {
      return;
    }

    // If jump is more than 1 or we looped, do the placeholder + reorder trick
    if (!instant && !isAdjacentSlide && visibleSlides.length === 1 && currentSlide) {
      await this.#handleNonAdjacentSlideTransition(targetSlide, currentSlide, requestedIndex, current);
    }

    const slide = slides[index];
    if (!slide) {
      return;
    }

    const previousIndex = this.current;
    const userInitiated = event !== null && event !== undefined;

    this.#commitSlideSelection(slide, index, previousIndex, instant, userInitiated);
  }

  /**
   * Commits the final slide selection: updates ARIA, scrolls to the slide,
   * updates the current index, centres the thumbnail, and dispatches the select event.
   * @param {Element} slide - The slide element to select
   * @param {number} index - The target slide index
   * @param {number} previousIndex - The previously selected slide index
   * @param {boolean} instant - Whether to skip animation
   * @param {boolean} userInitiated - Whether the selection was triggered by a user action
   */
  #commitSlideSelection(slide, index, previousIndex, instant, userInitiated) {
    slide.setAttribute(ATTR_ARIA_HIDDEN, "false");
    slide.toggleAttribute(ATTR_INERT, false);

    // Always scroll the viewport to the slide AND update the current index, for both
    // instant and smooth selections. `set current` only updates the dot/counter/aria —
    // it never scrolls — and the Scroller's onScroll→#sync resync is suppressed for
    // programmatic scrolls (userEvent=false), so splitting these across instant/smooth
    // branches froze the carousel for reduced-motion users and left `current` stale on
    // animated selects.
    // Non-adjacent animated transitions temporarily move the target next to the current
    // slide. Keep the resolved logical index instead of reading that transient DOM order.
    // The index is committed BEFORE the scroll: an instant scroll runs the visibility pass
    // synchronously, and that pass must already see the new slide as current.
    this.current = index;
    this.#scroll?.to(/** @type {HTMLElement} */ (slide), { instant });

    this.#centerSelectedThumbnail(index, instant ? "instant" : "smooth");
    this.dispatchEvent(
      new SlideshowSelectEvent({
        index,
        previousIndex,
        userInitiated,
        slide,
        trigger: "select",
        id: slide.getAttribute(ATTR_SLIDE_ID),
      }),
    );
  }

  /**
   * Advances to the next slide.
   * @param {Event} [event] - The event that triggered the next slide.
   * @param {Object} [options] - The options for the next slide.
   * @param {boolean} [options.animate=true] - Whether to animate the next slide.
   */
  next(event, options) {
    event?.preventDefault();
    this.select(this.nextIndex, event, options);
  }

  /**
   * Goes back to the previous slide.
   * @param {Event} [event] - The event that triggered the previous slide.
   * @param {Object} [options] - The options for the previous slide.
   * @param {boolean} [options.animate=true] - Whether to animate the previous slide.
   */
  previous(event, options) {
    event?.preventDefault();
    this.select(this.previousIndex, event, options);
  }

  /**
   * Starts automatic slide playback.
   * @param {number} [interval] - The time interval in seconds between slides.
   */
  play(interval = this.autoplayInterval || 0) {
    if (this.#interval) {
      return;
    }

    this.paused = false;
    // While auto-rotating, suppress the live region so assistive tech isn't announced a slide
    // change on every tick (WAI-ARIA carousel pattern: aria-live "off" during autoplay).
    this.setAttribute("aria-live", "off");

    this.#interval = /** @type {any} */ (
      setInterval(() => {
        if (this.matches(":hover") || document.hidden) {
          return;
        }

        this.next();
      }, interval)
    );
  }

  /**
   * Pauses automatic slide playback.
   */
  pause() {
    this.paused = true;
    // Paused → restore a polite live region so a manual slide change IS announced.
    this.setAttribute("aria-live", "polite");
    this.suspend();
  }

  get paused() {
    return this.hasAttribute("paused");
  }

  set paused(value) {
    if (value) {
      this.setAttribute("paused", "");
    } else {
      this.removeAttribute("paused");
    }
  }

  /**
   * Suspends automatic slide playback.
   */
  suspend() {
    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  /**
   * Resumes automatic slide playback if autoplay is enabled.
   */
  resume() {
    if (!this.autoplay || this.paused) {
      return;
    }

    this.pause();
    this.play();
  }

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  get autoplayInterval() {
    const interval = this.getAttribute("autoplay");
    const value = Number.parseInt(`${interval}`, 10);

    if (Number.isNaN(value)) {
      return null;
    }

    return value * 1000;
  }

  get current() {
    return this.#current;
  }

  /**
   * Sets the current slide index and update the DOM
   * @type {number}
   */
  set current(value) {
    const { current, thumbnails, dots, slides, previous, next } = this.refs;

    this.#current = value;

    if (current) {
      current.textContent = `${value + 1}`;
    }

    for (const controls of [thumbnails, dots]) {
      controls?.forEach((el, i) => el.setAttribute("aria-current", `${i === value}`));
    }

    if (previous) {
      previous.disabled = Boolean(!this.infinite && value === 0);
    }
    if (next) {
      next.disabled = Boolean(!this.infinite && slides && this.nextIndex >= slides.length);
    }
  }

  get infinite() {
    return this.getAttribute("infinite") !== null;
  }

  get visibleSlides() {
    return this.#visibleSlides;
  }

  get previousIndex() {
    const { current, visibleSlides } = this;
    const modifier = Math.max(visibleSlides.length, 1);

    return current - modifier;
  }

  get nextIndex() {
    const { current, visibleSlides } = this;
    const modifier = Math.max(visibleSlides.length, 1);

    return current + modifier;
  }

  get atStart() {
    const { current, slides } = this;

    return slides?.length ? current === 0 : false;
  }

  get atEnd() {
    const { current, slides } = this;

    return slides?.length ? current === slides.length - 1 : false;
  }

  /**
   * Whether the slideshow is disabled.
   * @type {boolean}
   */
  get disabled() {
    return (
      this.getAttribute("disabled") === "true" || (this.hasAttribute("mobile-disabled") && !mediaQueryLarge.matches)
    );
  }

  /**
   * Sets the disabled attribute.
   * @param {boolean} value - The value to set the disabled attribute to.
   */
  set disabled(value) {
    this.setAttribute("disabled", String(value));
  }

  /**
   * Setup the slideshow without controls for zero or one slides
   */
  #setupSlideshowWithoutControls() {
    this.current = 0;
    if (this.hasAttribute(ATTR_AUTO_HIDE_CONTROLS)) {
      const { slideshowControls } = this.refs;
      if (slideshowControls instanceof HTMLElement) {
        slideshowControls.hidden = true;
      }
    }

    if (this.refs.slides?.[0]) {
      this.refs.slides[0].setAttribute(ATTR_ARIA_HIDDEN, "false");
      this.refs.slides[0].toggleAttribute(ATTR_INERT, false);
    }
  }

  /**
   * Setup the slideshow with controls for when there are multiple slides
   */
  #setupSlideshow() {
    // Setup IntersectionObserver first for efficient visibility tracking
    this.#setupIntersectionObserver();

    // Setup the scroll instance
    const { scroller } = this.refs;
    this.#scroll = new Scroller(scroller, {
      onScroll: this.#handleScroll,
      onScrollStart: this.#onTransitionInit,
      onScrollEnd: this.#onTransitionEnd,
    });

    scroller.addEventListener("mousedown", this.#handleMouseDown);

    this.addEventListener("mouseenter", this.suspend);
    this.addEventListener("mouseleave", this.resume);
    this.addEventListener("pointerenter", this.#handlePointerEnter);
    this.addEventListener("keydown", this.#handleKeyDown);
    document.addEventListener("visibilitychange", this.#handleVisibilityChange);

    this.#updateControlsVisibility();

    this.disabled = this.isNested || this.disabled;

    this.resume();

    this.current = this.initialSlideIndex;

    // Batch reads and writes to the DOM
    scheduler.schedule(() => {
      let visibleSlidesAmount = 0;
      const initialSlideId = this.initialSlide?.getAttribute(ATTR_SLIDE_ID);

      // Wait for next frame to ensure layout is fully calculated before setting initial scroll position
      // This prevents race conditions on Safari mobile when section_width is 'full-width'
      requestAnimationFrame(() => {
        if (this.initialSlideIndex !== 0 && initialSlideId) {
          this.select({ id: initialSlideId }, undefined, { animate: false });
          visibleSlidesAmount = 1;
        } else {
          visibleSlidesAmount = this.#updateVisibleSlides();
          if (visibleSlidesAmount === 0) {
            this.select(0, undefined, { animate: false });
            visibleSlidesAmount = 1;
          }
        }

        // Layout is now settled — set the scroller's initial keyboard focusability.
        this.#updateScrollerFocusability();
      });

      this.#resizeObserver = new ResizeObserver(async () => {
        if (viewTransition.current) {
          await viewTransition.current;
        }

        if (visibleSlidesAmount > 1) {
          this.#updateVisibleSlides();
        }

        if (this.hasAttribute(ATTR_AUTO_HIDE_CONTROLS)) {
          this.#updateControlsVisibility();
        }

        // Overflow can change on resize — re-evaluate keyboard focusability for every
        // slideshow (not just [auto-hide-controls] ones). a11y: scrollable-region-focusable.
        this.#updateScrollerFocusability();
      });

      this.#resizeObserver.observe(this.refs.slideshowContainer);
    });
  }

  get slides() {
    return this.refs.slides?.filter(slide => !slide.hasAttribute("hidden") || slide.hasAttribute(ATTR_REVEAL));
  }

  /**
   * The initial slide index.
   * @type {number}
   */
  get initialSlideIndex() {
    const initialSlide = this.getAttribute(ATTR_INITIAL_SLIDE);
    if (initialSlide === null) {
      return 0;
    }

    return Number.parseInt(initialSlide, 10);
  }

  #updateControlsVisibility() {
    if (!this.hasAttribute(ATTR_AUTO_HIDE_CONTROLS)) {
      return;
    }

    const { scroller, slideshowControls } = this.refs;

    if (!(slideshowControls instanceof HTMLElement)) {
      return;
    }

    slideshowControls.hidden = scroller.scrollWidth <= scroller.offsetWidth;
  }

  /**
   * Toggle the scroll container's keyboard focusability based on whether it actually overflows.
   *
   * A horizontally-scrolling region with no focusable content is unreachable by keyboard-only
   * users (axe: scrollable-region-focusable, WCAG 2.1.1). When the scroller overflows we make it
   * focusable (tabindex=0) and give it an accessible name so screen-reader users know what the
   * focusable region is; when it fits there is nothing to scroll, so we drop it back to
   * tabindex=-1 to keep it out of the tab order.
   *
   * Runs for EVERY slideshow — independent of [auto-hide-controls], which only governs control
   * visibility. Overflow only changes on connect and resize, so this is hooked into the initial
   * layout pass and the ResizeObserver (NOT slide changes, which never alter overflow).
   */
  #updateScrollerFocusability() {
    const { scroller } = this.refs;

    if (!(scroller instanceof HTMLElement)) {
      return;
    }

    const overflowing = scroller.scrollWidth > scroller.clientWidth;

    if (overflowing) {
      scroller.setAttribute("tabindex", "0");
      // Only name the region if it isn't already named, so an author-provided label wins.
      if (!scroller.hasAttribute(ATTR_ARIA_LABEL) && !scroller.hasAttribute("aria-labelledby")) {
        scroller.setAttribute(ATTR_ARIA_LABEL, Theme.translations?.slideshow || "Slideshow");
        this.#scrollerLabelApplied = true;
      }
    } else {
      scroller.setAttribute("tabindex", "-1");
      // If the region later stops overflowing (e.g. a wider resize), drop the name WE added so a
      // non-tabbable scroller isn't left carrying a stale aria-label — but never strip an
      // author-provided label (only labels this component applied are removed).
      if (this.#scrollerLabelApplied) {
        scroller.removeAttribute(ATTR_ARIA_LABEL);
        this.#scrollerLabelApplied = false;
      }
    }
  }

  /**
   * Setup IntersectionObserver for efficient visibility tracking of slides
   */
  #setupIntersectionObserver() {
    const { slides, scroller } = this.refs;
    if (!slides?.length) {
      return;
    }

    if (this.#intersectionObserver) {
      this.#intersectionObserver.disconnect();
    }

    this.#intersectionObserver = new IntersectionObserver(
      entries => {
        const allEntries = [
          ...entries,
          ...(this.#intersectionObserver ? this.#intersectionObserver.takeRecords() : []),
        ];

        for (const entry of allEntries) {
          const slide = /** @type {HTMLElement} */ (entry.target);
          const isCurrentlyVisible = this.#visibleSlides.includes(slide);
          const shouldBeVisible = entry.intersectionRatio >= SLIDE_VISIBLITY_THRESHOLD;

          if (shouldBeVisible && !isCurrentlyVisible) {
            this.#visibleSlides.push(slide);
          } else if (!shouldBeVisible && isCurrentlyVisible) {
            const index = this.#visibleSlides.indexOf(slide);
            if (index !== -1) {
              this.#visibleSlides.splice(index, 1);
            }
          }
        }

        this.#visibleSlides.sort((slideA, slideB) => slides.indexOf(slideA) - slides.indexOf(slideB));
        this.#updateVisibleSlides();
      },
      {
        root: scroller,
        threshold: SLIDE_VISIBLITY_THRESHOLD,
        // Add small margin to account for sub-pixel rendering
        rootMargin: "1px",
      },
    );

    // Observe all slides - observer will fire initial callback asynchronously
    slides.forEach(slide => {
      this.#intersectionObserver?.observe(slide);
    });
  }

  /**
   * Centers the selected thumbnail in the thumbnails container
   * @param {number} index - The index of the selected thumbnail
   * @param {ScrollBehavior} [behavior] - The scroll behavior.
   */
  #centerSelectedThumbnail(index, behavior = "smooth") {
    const selectedThumbnail = this.refs.thumbnails?.[index];
    if (!selectedThumbnail) {
      return;
    }

    const { thumbnailsContainer } = this.refs;
    if (!thumbnailsContainer || !(thumbnailsContainer instanceof HTMLElement)) {
      return;
    }

    const { slideshowControls } = this.refs;
    if (!slideshowControls || !(slideshowControls instanceof HTMLElement)) {
      return;
    }

    scrollIntoView(selectedThumbnail, {
      behavior,
      ancestor: thumbnailsContainer,
      block: "center",
      inline: "center",
    });
  }

  #updateVisibleSlides() {
    const { slides } = this;
    if (!slides || slides.length === 0) {
      return 0;
    }

    const visibleSlides = this.visibleSlides;

    // Batch writes to the DOM
    scheduler.schedule(() => {
      // The selected slide is never inert: mid-transition (or before layout settles) the
      // observer can still report it off-screen, and marking it inert would drop keyboard
      // focus that just moved there.
      const currentSlide = this.slides?.[this.current];
      // Update aria-hidden based on visibility
      slides.forEach(slide => {
        const isVisible = visibleSlides.includes(slide) || slide === currentSlide;
        slide.setAttribute(ATTR_ARIA_HIDDEN, `${!isVisible}`);
        slide.toggleAttribute(ATTR_INERT, !isVisible);
      });
    });

    return visibleSlides.length;
  }
}

if (!customElements.get(ATTR_SLIDESHOW_COMPONENT)) {
  customElements.define(ATTR_SLIDESHOW_COMPONENT, Slideshow);
}
